'use client'

import { useRef } from 'react'
import Link from 'next/link'
import { useInView } from '@/hooks/useInView'
import {
  ArrowRight,
  Check,
  X,
  DollarSign,
  Users,
  Bot,
  Hammer,
  Zap,
  Shield,
  ArrowDown,
  Infinity as InfinityIcon,
  Crown,
  TrendingUp,
  Clock,
  AlertTriangle,
} from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaLink } from '@/components/conversion/AttributedCtaLink'
import { AnimatedCounter } from '@/components/ui/AnimatedCounter'
import { ROICalculator } from '@/components/home/ROICalculator'
import { RelatedGuides } from '@/components/seo/RelatedGuides'
import { WHY_LINKS } from '@/lib/internal-links'

/* ── Data ─────────────────────────────────────────────────── */

const replacedTools = [
  { name: 'JobNimbus', category: 'CRM', cost: '$250/mo' },
  { name: 'Jobber', category: 'Scheduling', cost: '$229/mo' },
  { name: 'RoofR', category: 'Estimating', cost: '$299/mo' },
  { name: 'GoHighLevel', category: 'Marketing', cost: '$297/mo' },
  { name: 'PandaDoc', category: 'Proposals', cost: '$49/mo' },
  { name: 'Calendly', category: 'Scheduling', cost: '$16/mo' },
  { name: 'Mailchimp', category: 'Email', cost: '$59/mo' },
  { name: 'Slack / Teams', category: 'Team Chat', cost: '$50+/mo' },
]

const differentiators = [
  {
    icon: InfinityIcon,
    title: 'Unlimited Users',
    description: 'No per-seat pricing. Add your entire team — office staff, sales reps, crews — at no extra cost. Scale without limits.',
  },
  {
    icon: Bot,
    title: 'Sierra AI Agent',
    description: 'Your 24/7 AI team member. Sierra answers calls, qualifies leads, books appointments, and follows up — so your team can focus on selling and installing. Included on the Pro plan.',
  },
  {
    icon: Hammer,
    title: 'Built for Roofing Reality',
    description: "We didn't read about roofing in a book. Our founders ran roofing companies and built the tool they wished existed. Every feature is battle-tested.",
  },
  {
    icon: Zap,
    title: 'All-In-One Platform',
    description: 'CRM, estimating, proposals, scheduling, invoicing, marketing, and AI — all in one system. No more juggling 8 different logins.',
  },
  {
    icon: Shield,
    title: 'Native Integrations',
    description: 'Deep, built-in integrations with EagleView, ABC Supply, SRS Distribution, QuickBooks, Google, and Twilio. Native data flow — plus MCP access available on request.',
  },
  {
    icon: Crown,
    title: 'Guided Onboarding',
    description: 'Our Pro plan includes an onboarding session plus priority chat and phone support, so your team hits the ground running. Enterprise adds a dedicated Customer Success Manager.',
  },
]

const painPoints = [
  {
    pain: '8+ different logins every day',
    painIcon: AlertTriangle,
    solution: 'One platform. One login. Everything you need.',
    solutionIcon: Zap,
  },
  {
    pain: 'Tools that don\'t talk to each other',
    painIcon: X,
    solution: 'Native integrations with real-time data flow.',
    solutionIcon: Shield,
  },
  {
    pain: '$2,000+/mo in disconnected subscriptions',
    painIcon: DollarSign,
    solution: 'Everything included on Pro for $997/mo, unlimited users.',
    solutionIcon: TrendingUp,
  },
  {
    pain: 'Missed calls and cold leads',
    painIcon: Clock,
    solution: 'Sierra AI answers 24/7, qualifies, and books instantly.',
    solutionIcon: Bot,
  },
]

const withBuilderLyncFeatures = [
  'CRM & Pipeline Management',
  'AI Lead Nurturing & Follow-Ups',
  'Estimating & Proposals',
  'Scheduling & Dispatch',
  'Marketing Automation',
  'Invoicing & Payments',
  'Storm Intelligence & Canvassing',
  'Sierra AI Chat, SMS & Voice Agent',
]

const totalOtherCost = '$2,000+'

const stats = [
  { value: 50, prefix: '', suffix: '%', label: 'Cost Reduction' },
  { value: 6, prefix: '', suffix: '+', label: 'Tools Replaced' },
  { value: 0, prefix: '$', suffix: '', label: 'Per-Seat Fees', displayValue: '0' },
  { value: 14, prefix: '', suffix: ' Days', label: 'White Glove Setup (Add-On)' },
]

/* ── CSS bounce animation for arrows ─────────────────────── */
const bounceXStyle: React.CSSProperties = {
  animation: 'bounce-x 1.5s ease-in-out infinite',
}
const bounceYStyle: React.CSSProperties = {
  animation: 'bounce-y 1.5s ease-in-out infinite',
}

/* ── CTA Block ────────────────────────────────────────────── */

function MidCTA({ headline, sub }: { headline: string; sub: string }) {
  const { ref, isInView } = useInView()

  return (
    <div
      ref={ref}
      className={`anim-fade-in-up ${isInView ? 'in-view' : ''} text-center py-16`}
    >
      <p className="text-zinc-400 mb-4">{sub}</p>
      <AttributedCtaLink
        href={BOOK_A_DEMO_URL}
        page="why-builderlync"
        cta={headline}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary text-base px-7 py-3.5"
      >
        {headline}
        <ArrowRight size={18} />
      </AttributedCtaLink>
    </div>
  )
}

/* ── Page ─────────────────────────────────────────────────── */

export default function WhyBuilderLyncPage() {
  const { ref: heroRef, isInView: heroInView } = useInView()
  const { ref: painRef, isInView: painInView } = useInView()
  const { ref: statsRef, isInView: statsInView } = useInView()
  const { ref: toolsRef, isInView: toolsInView } = useInView()
  const { ref: costRef, isInView: costInView } = useInView()
  const { ref: diffRef, isInView: diffInView } = useInView()
  const { ref: ctaRef, isInView: ctaInView } = useInView()

  return (
    <div className="min-h-screen">
      {/* Keyframes for bouncing arrows */}
      <style>{`
        @keyframes bounce-x { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(4px); } }
        @keyframes bounce-y { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(4px); } }
      `}</style>

      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="section" ref={heroRef}>
        <div className="section-inner text-center">
          <div
            className={`anim-fade-in-up ${heroInView ? 'in-view' : ''} inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/50 backdrop-blur-sm mb-8`}
          >
            <Crown size={14} className="text-red-500" />
            <span className="text-sm text-zinc-400">The smarter choice</span>
          </div>

          <h1
            className={`anim-fade-in-up anim-delay-1 ${heroInView ? 'in-view' : ''} text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-[-0.03em] mb-6`}
          >
            Why Contractors Choose{' '}
            <span className="gradient-text-red">BuilderLync</span>
            <br />
            Over Everything Else
          </h1>

          <p
            className={`anim-fade-in-up anim-delay-2 ${heroInView ? 'in-view' : ''} text-lg text-zinc-400 max-w-2xl mx-auto mb-10`}
          >
            Stop paying for 8+ disconnected tools that don&apos;t talk to each other.
            BuilderLync replaces your entire tech stack with one AI-powered platform.
          </p>

          <AttributedCtaLink
            className={`anim-fade-in-up anim-delay-3 ${heroInView ? 'in-view' : ''} btn-primary text-base px-7 py-3.5 glow-red-sm inline-flex`}
            href={BOOK_A_DEMO_URL}
            page="why-builderlync"
            cta="Hero — See It In Action"
            target="_blank"
            rel="noopener noreferrer"
          >
            See It In Action
            <ArrowRight size={18} />
          </AttributedCtaLink>
        </div>
      </section>

      {/* ── Stats Bar ─────────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12 md:pb-16" ref={statsRef}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`anim-fade-in-up anim-delay-${i + 1} ${statsInView ? 'in-view' : ''} glass-card p-6 text-center`}
            >
              <div className="text-3xl sm:text-4xl font-extrabold gradient-text-red mb-1">
                <AnimatedCounter
                  value={stat.value}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  duration={1800}
                  className="gradient-text-red"
                />
              </div>
              <p className="text-xs sm:text-sm text-zinc-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pain → Solution (Before / After) ──────────────── */}
      <section className="section" ref={painRef}>
        <div className="section-inner">
          <h2
            className={`anim-fade-in-up ${painInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4`}
          >
            The Problem With <span className="gradient-text-red">Your Current Stack</span>
          </h2>
          <p
            className={`anim-fade-in-up anim-delay-1 ${painInView ? 'in-view' : ''} text-zinc-400 text-center mb-12 max-w-xl mx-auto`}
          >
            Sound familiar? Here&apos;s what contractors deal with every day — and how BuilderLync fixes it.
          </p>

          <div className="space-y-6 max-w-4xl mx-auto">
            {painPoints.map((point, i) => {
              const PainIcon = point.painIcon
              const SolIcon = point.solutionIcon
              return (
                <div
                  key={i}
                  className={`anim-fade-in-up anim-delay-${Math.min(i + 2, 8)} ${painInView ? 'in-view' : ''} grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-center`}
                >
                  {/* Pain */}
                  <div className="glass-card p-5 flex items-center gap-4 border-zinc-800">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <PainIcon size={18} className="text-zinc-400" />
                    </div>
                    <p className="text-sm text-zinc-400 line-through decoration-zinc-600">{point.pain}</p>
                  </div>

                  {/* Arrow */}
                  <div className="hidden md:flex items-center justify-center">
                    <div style={bounceXStyle}>
                      <ArrowRight size={20} className="text-red-500" />
                    </div>
                  </div>
                  <div className="flex md:hidden items-center justify-center">
                    <div style={bounceYStyle}>
                      <ArrowDown size={18} className="text-red-500" />
                    </div>
                  </div>

                  {/* Solution */}
                  <div className="glass-card p-5 flex items-center gap-4 border-red-600/20 bg-red-600/[0.02]">
                    <div className="w-10 h-10 rounded-lg bg-red-600/10 flex items-center justify-center flex-shrink-0">
                      <SolIcon size={18} className="text-red-500" />
                    </div>
                    <p className="text-sm text-white font-medium">{point.solution}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <MidCTA headline="Book a Demo" sub="Ready to simplify your tech stack?" />

      {/* ── Tool Replacement Section ──────────────────────── */}
      <section className="section" ref={toolsRef}>
        <div className="section-inner">
          <h2
            className={`anim-fade-in-up ${toolsInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4`}
          >
            Replace <span className="gradient-text-red">Your Entire Stack</span>
          </h2>
          <p
            className={`anim-fade-in-up anim-delay-1 ${toolsInView ? 'in-view' : ''} text-zinc-400 text-center mb-10 max-w-xl mx-auto`}
          >
            All the tools you&apos;re paying for separately — combined into one platform.
          </p>

          {/* Tools Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
            {replacedTools.map((tool, i) => (
              <div
                key={tool.name}
                className={`anim-fade-in-up anim-delay-${Math.min(i + 1, 8)} ${toolsInView ? 'in-view' : ''} glass-card p-4 relative group`}
              >
                {/* Strikethrough overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-full h-[1px] bg-red-500/60 rotate-[-8deg]" />
                </div>
                <div className="relative">
                  <p className="text-sm font-semibold text-white group-hover:text-zinc-400 transition-colors">{tool.name}</p>
                  <p className="text-xs text-zinc-600">{tool.category}</p>
                  <p className="text-xs text-red-500/80 mt-1">{tool.cost}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center mb-12">
            <div style={bounceYStyle}>
              <ArrowDown size={32} className="text-red-500" />
            </div>
          </div>

          {/* BuilderLync Card */}
          <div
            className={`anim-fade-in-up ${toolsInView ? 'in-view' : ''} glass-card-premium p-8 md:p-12 text-center border-red-600/30 glow-red max-w-2xl mx-auto`}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
              BuilderLync
            </h3>
            <p className="text-zinc-400 mb-6">All of the above, in one platform.</p>
            <p className="text-xs font-semibold uppercase tracking-widest text-red-500 mb-2">Pro plan</p>
            <div className="flex items-baseline justify-center gap-2 mb-6">
              <span className="text-5xl md:text-6xl font-extrabold gradient-text-red">$997</span>
              <span className="text-zinc-400">/mo</span>
            </div>
            <p className="text-sm text-zinc-400 mb-2">Unlimited users · No per-seat fees · No contracts</p>
            <p className="text-sm text-zinc-500 mb-6">
              Don&apos;t need the AI stack? Starter is $497/mo.
            </p>
            <Link
              href="/pricing"
              className="btn-primary text-base px-7 py-3.5 glow-red-sm"
            >
              See Plans & Pricing
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Cost Comparison ───────────────────────────────── */}
      <section className="section" ref={costRef}>
        <div className="section-inner">
          <h2
            className={`anim-fade-in-up ${costInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-8 sm:mb-10`}
          >
            The <span className="gradient-text-red">Math</span> Speaks For Itself
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Without BuilderLync */}
            <div
              className={`anim-fade-in-left ${costInView ? 'in-view' : ''} glass-card p-8`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                  <X size={20} className="text-zinc-400" />
                </div>
                <h3 className="text-xl font-bold text-zinc-400">Without BuilderLync</h3>
              </div>
              <ul className="space-y-3 mb-6">
                {replacedTools.slice(0, 6).map((tool) => (
                  <li key={tool.name} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">{tool.name}</span>
                    <span className="text-zinc-400">{tool.cost}</span>
                  </li>
                ))}
                <li className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">+ 6 more tools...</span>
                  <span className="text-zinc-400">$500+/mo</span>
                </li>
              </ul>
              <div className="border-t border-zinc-800 pt-4 flex items-center justify-between">
                <span className="text-zinc-400 font-semibold">Total</span>
                <span className="text-2xl font-bold text-white">{totalOtherCost}<span className="text-sm text-zinc-400">/mo</span></span>
              </div>
            </div>

            {/* With BuilderLync */}
            <div
              className={`anim-fade-in-right anim-delay-1 ${costInView ? 'in-view' : ''} glass-card-premium p-8 border-red-600/30 glow-red-sm`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                  <Check size={20} className="text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-white">With BuilderLync Pro</h3>
              </div>
              <ul className="space-y-3 mb-6">
                {withBuilderLyncFeatures.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-sm text-zinc-300">
                    <Check size={14} className="text-red-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="border-t border-red-600/20 pt-4 flex items-center justify-between">
                <span className="text-zinc-400 font-semibold">Total</span>
                <span className="text-2xl font-bold gradient-text-red">$997<span className="text-sm text-zinc-400">/mo</span></span>
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Starter is $497/mo without Storm Intelligence or the AI stack.
              </p>
            </div>
          </div>

          {/* Savings callout */}
          <div
            className={`anim-fade-in-up anim-delay-2 ${costInView ? 'in-view' : ''} text-center mt-12`}
          >
            <p className="text-lg text-zinc-400">
              Save <span className="text-white font-bold">50% or more</span>{' '}
              while getting more features and zero per-seat fees.
            </p>
          </div>
        </div>
      </section>

      {/* ── Key Differentiators ───────────────────────────── */}
      <section className="section" ref={diffRef}>
        <div className="section-inner">
          <h2
            className={`anim-fade-in-up ${diffInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4`}
          >
            What Sets Us <span className="gradient-text-red">Apart</span>
          </h2>
          <p
            className={`anim-fade-in-up anim-delay-1 ${diffInView ? 'in-view' : ''} text-zinc-400 text-center mb-10 max-w-xl mx-auto`}
          >
            BuilderLync isn&apos;t just another SaaS tool. It&apos;s the operating system your roofing business deserves.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {differentiators.map((diff, i) => {
              const Icon = diff.icon
              return (
                <div
                  key={diff.title}
                  className={`anim-fade-in-up anim-delay-${Math.min(i + 1, 8)} ${diffInView ? 'in-view' : ''} glass-card-premium p-8 group`}
                >
                  <div className="w-12 h-12 rounded-xl bg-red-600/10 flex items-center justify-center mb-5 group-hover:bg-red-600/15 transition-colors">
                    <Icon size={24} className="text-red-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{diff.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{diff.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── ROI Calculator ─────────────────────────────────── */}
      <ROICalculator />

      <RelatedGuides
        badge="Explore"
        cta="Learn more"
        heading="Head-to-Head Comparisons"
        subheading="How BuilderLync stacks up against the tools it replaces."
        guides={WHY_LINKS}
      />

      {/* ── Bottom CTA ────────────────────────────────────── */}
      <section className="section" ref={ctaRef}>
        <div className="section-inner text-center">
          <h2
            className={`anim-fade-in-up ${ctaInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-5xl font-bold mb-4`}
          >
            Stop Overpaying.{' '}
            <span className="gradient-text-red">Start Building.</span>
          </h2>
          <p
            className={`anim-fade-in-up anim-delay-1 ${ctaInView ? 'in-view' : ''} text-zinc-400 mb-8 max-w-lg mx-auto`}
          >
            Join the contractors who ditched their franken-stack for one platform that does it all.
          </p>
          <div
            className={`anim-fade-in-up anim-delay-2 ${ctaInView ? 'in-view' : ''} flex flex-col sm:flex-row items-center justify-center gap-4`}
          >
            <AttributedCtaLink
              href={BOOK_A_DEMO_URL}
              page="why-builderlync"
              cta="Bottom CTA — Book a Demo"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-base px-7 py-3.5 glow-red-sm"
            >
              Book a Demo
              <ArrowRight size={18} />
            </AttributedCtaLink>
            <Link href="/pricing" className="btn-ghost text-base px-7 py-3.5">
              <DollarSign size={16} />
              See Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
